
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertTriangle,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  Minus as TrendingFlat,
  Activity,
  UserMinus,
  MessageSquare
} from 'lucide-react';

interface Player {
  id: string;
  name: string;
  avatar?: string;
  status: 'active' | 'break' | 'needs_attention';
  currentMetrics: {
    shotsToday: number;
    averageDistance: number;
    consistencyScore: number;
    lastShotQuality: 'excellent' | 'good' | 'needs_work';
    improvementTrend: 'up' | 'stable' | 'down';
  };
  alerts: string[];
}

interface PlayerTileProps {
  player: Player;
  onRemove: () => void;
  onMessage?: () => void;
  onViewDetails?: () => void;
}

export function PlayerTile({ player, onRemove, onMessage, onViewDetails }: PlayerTileProps) {
  const getStatusColor = (status: Player['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'break':
        return 'bg-yellow-500';
      case 'needs_attention':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: Player['status']) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'break':
        return 'On Break';
      case 'needs_attention':
        return 'Needs Attention';
      default:
        return status;
    }
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'good':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'needs_work':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'stable':
        return <TrendingFlat className="w-4 h-4 text-gray-500" />;
      default:
        return <TrendingFlat className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatAlertText = (alert: string) => {
    return alert.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card className={`relative transition-all duration-200 hover:shadow-md ${
      player.status === 'needs_attention' ? 'ring-2 ring-red-200' : ''
    }`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Avatar className="h-10 w-10">
                <AvatarImage src={player.avatar} alt={player.name} />
                <AvatarFallback>
                  {player.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${getStatusColor(player.status)} border-2 border-white`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm truncate">{player.name}</h3>
              <Badge variant="outline" className="text-xs mt-1">
                {getStatusText(player.status)}
              </Badge>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onViewDetails && (
                <DropdownMenuItem onClick={onViewDetails}>
                  <Activity className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
              )}
              {onMessage && (
                <DropdownMenuItem onClick={onMessage}>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Send Message
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onRemove} className="text-red-600">
                <UserMinus className="mr-2 h-4 w-4" />
                Remove from Session
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-4">
        {/* Alerts */}
        {player.alerts.length > 0 && (
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <div className="flex-1">
              {player.alerts.map((alert, index) => (
                <Badge key={index} variant="outline" className="text-xs mr-1 text-orange-600 border-orange-200">
                  {formatAlertText(alert)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Shots Today</span>
              <span className="font-medium">{player.currentMetrics.shotsToday}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg Distance</span>
              <span className="font-medium">{player.currentMetrics.averageDistance}m</span>
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Consistency</span>
              <span className="font-medium">{player.currentMetrics.consistencyScore}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Trend</span>
              {getTrendIcon(player.currentMetrics.improvementTrend)}
            </div>
          </div>
        </div>

        {/* Last Shot Quality */}
        <div className="space-y-2">
          <span className="text-xs text-muted-foreground">Last Shot Quality</span>
          <Badge 
            variant="outline" 
            className={`text-xs ${getQualityColor(player.currentMetrics.lastShotQuality)}`}
          >
            {player.currentMetrics.lastShotQuality.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>

        {/* Progress Bar for Consistency */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Consistency Score</span>
            <span className="font-medium">{player.currentMetrics.consistencyScore}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                player.currentMetrics.consistencyScore >= 80 ? 'bg-green-500' :
                player.currentMetrics.consistencyScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(player.currentMetrics.consistencyScore, 100)}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}